export const CONTRACT_VERSION = "conference-connector-contract-v1";
export const SNAPSHOT_VERSION = "conference-snapshot-v1";
export const CURRENT_COMPONENT_VERSIONS = Object.freeze({
  connector: "1.0.0",
  adapter: "1.0.0",
  source: "1.0.0",
});
const SEMANTIC_VERSION = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;
const PROJECT_ID = /^[a-z][a-z0-9-]{4,29}$/;
const CONFERENCE_KEY = /^[a-z0-9][a-z0-9-]{2,63}$/;
const REGION = /^[a-z]+-[a-z]+[0-9]+$/;
const COUNTRY = /^[A-Z]{2}$/;
const FORBIDDEN_KEYS =
  /^(?:attendee|delegate|participant|person|user|email|phone|mobile|invoiceId|registrationId|sponsorId|fullName|firstName|lastName|displayName|contact|contactName|contactEmail|contactPhone|address|nationalId|passport|idNumber|iban|bankAccount|accountNumber|ipAddress|userAgent|deviceId)$/i;
const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const SAUDI_IBAN = /\bSA(?:\s?\d){22}\b/i;
const SAUDI_NATIONAL_ID = /(?:^|\D)[12]\d{9}(?:\D|$)/;
const IPV4 = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
const CANONICAL_CLOUD_RUN_HOST =
  /^(?<service>[a-z][a-z0-9-]{0,62})-(?<projectNumber>\d{6,20})\.(?<region>[a-z]+-[a-z]+[0-9]+)\.run\.app$/;

function containsPhoneNumber(value) {
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return false;
  const candidates = value.match(/\+?[\d][\d ()-]{7,}[\d]/g) ?? [];
  return candidates.some((candidate) => {
    const digits = candidate.replace(/\D/g, "");
    return digits.length >= 9 && digits.length <= 15;
  });
}

function containsIpv4Address(value) {
  return [...value.matchAll(IPV4)].some((match) =>
    match[0].split(".").every((octet) => Number(octet) <= 255),
  );
}

function containsPersonalDataValue(value) {
  return (
    EMAIL.test(value) ||
    SAUDI_IBAN.test(value) ||
    SAUDI_NATIONAL_ID.test(value) ||
    containsPhoneNumber(value) ||
    containsIpv4Address(value)
  );
}

export function cloudRunEndpointForPrivacyScan(value) {
  let endpoint;
  try {
    endpoint = new URL(value);
  } catch {
    return null;
  }
  const match = endpoint.hostname.match(CANONICAL_CLOUD_RUN_HOST);
  if (
    endpoint.protocol !== "https:" ||
    !match?.groups?.projectNumber ||
    endpoint.username ||
    endpoint.password ||
    endpoint.port
  ) {
    return null;
  }
  const sanitizedHostname = endpoint.hostname.replace(
    match.groups.projectNumber,
    "project-number",
  );
  return `${endpoint.protocol}//${sanitizedHostname}${endpoint.pathname}${endpoint.search}${endpoint.hash}`;
}

function containsPersonalDataAtPath(value, path) {
  if (path === "$.connector.snapshotEndpoint") {
    const sanitizedEndpoint = cloudRunEndpointForPrivacyScan(value);
    if (sanitizedEndpoint !== null) {
      return containsPersonalDataValue(sanitizedEndpoint);
    }
  }
  return containsPersonalDataValue(value);
}

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value, expected, path, errors, optional = []) {
  if (!isObject(value)) {
    errors.push(`${path} must be an object`);
    return false;
  }
  const expectedSet = new Set(expected);
  const optionalSet = new Set(optional);
  for (const key of expected)
    if (!(key in value) && !optionalSet.has(key))
      errors.push(`${path}.${key} is required`);
  for (const key of Object.keys(value))
    if (!expectedSet.has(key)) errors.push(`${path}.${key} is not allowed`);
  return true;
}

function validDate(value) {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function nonNegativeInteger(value) {
  return Number.isInteger(value) && value >= 0;
}

function calendarDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return false;
  const time = Date.parse(`${value}T00:00:00Z`);
  return (
    Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value
  );
}

export function validAnalyticsLifetime(value) {
  if (
    !isObject(value) ||
    Object.keys(value).length !== 4 ||
    !["availability", "totalUsers", "startDate", "endDate"].every((key) =>
      Object.hasOwn(value, key),
    )
  )
    return false;
  if (value.availability === "unavailable") {
    return (
      value.totalUsers === null &&
      value.startDate === null &&
      value.endDate === null
    );
  }
  return (
    value.availability === "available" &&
    Number.isSafeInteger(value.totalUsers) &&
    value.totalUsers >= 0 &&
    calendarDate(value.startDate) &&
    calendarDate(value.endDate) &&
    value.startDate <= value.endDate
  );
}

function scanPersonalData(value, path, errors) {
  if (Array.isArray(value))
    return value.forEach((item, index) =>
      scanPersonalData(item, `${path}[${index}]`, errors),
    );
  if (!isObject(value)) {
    if (typeof value === "string") {
      if (containsPersonalDataAtPath(value, path)) {
        errors.push(`${path} contains a prohibited personal-data value`);
      }
    }
    return;
  }
  for (const [key, nested] of Object.entries(value)) {
    if (FORBIDDEN_KEYS.test(key))
      errors.push(
        `${path}.${key} is a prohibited personal or source-record field`,
      );
    scanPersonalData(nested, `${path}.${key}`, errors);
  }
}

function validateCapability(value, path, errors) {
  if (!exactKeys(value, ["supported", "mode"], path, errors)) return;
  if (typeof value.supported !== "boolean")
    errors.push(`${path}.supported must be boolean`);
  if (!["automated", "unavailable"].includes(value.mode))
    errors.push(`${path}.mode is invalid`);
  if (value.supported !== (value.mode === "automated"))
    errors.push(`${path} supported and mode disagree`);
}

function validateComponentVersions(value, errors) {
  if (
    !exactKeys(
      value,
      ["connector", "adapter", "source"],
      "$.componentVersions",
      errors,
    )
  )
    return;
  for (const component of ["connector", "adapter", "source"]) {
    if (!SEMANTIC_VERSION.test(value[component] ?? "")) {
      errors.push(
        `$.componentVersions.${component} must be a semantic version`,
      );
    }
  }
}

function majorVersion(value) {
  const match =
    typeof value === "string" ? value.match(SEMANTIC_VERSION) : null;
  return match ? Number(match[1]) : null;
}

export function assessConnectorCompatibility(value) {
  if (!isObject(value)) {
    return Object.freeze({
      status: "invalid",
      reasonCode: "version_metadata_invalid",
    });
  }
  if (
    typeof value.contractVersion !== "string" ||
    typeof value.snapshotVersion !== "string"
  ) {
    return Object.freeze({
      status: "invalid",
      reasonCode: "version_metadata_invalid",
    });
  }
  if (value.contractVersion !== CONTRACT_VERSION) {
    return Object.freeze({
      status: "upgrade_required",
      reasonCode: "contract_version_unsupported",
    });
  }
  if (value.snapshotVersion !== SNAPSHOT_VERSION) {
    return Object.freeze({
      status: "upgrade_required",
      reasonCode: "snapshot_version_unsupported",
    });
  }
  if (!isObject(value.componentVersions)) {
    return Object.freeze({
      status: "invalid",
      reasonCode: "version_metadata_invalid",
    });
  }
  for (const component of ["connector", "adapter", "source"]) {
    const major = majorVersion(value.componentVersions[component]);
    if (major === null) {
      return Object.freeze({
        status: "invalid",
        reasonCode: "version_metadata_invalid",
      });
    }
    if (major !== 1) {
      return Object.freeze({
        status: "upgrade_required",
        reasonCode: `${component}_version_unsupported`,
      });
    }
  }
  return Object.freeze({ status: "compatible", reasonCode: null });
}

export function validateManifest(value) {
  const errors = [];
  if (
    !exactKeys(
      value,
      [
        "contractVersion",
        "snapshotVersion",
        "componentVersions",
        "projectId",
        "conference",
        "connector",
        "capabilities",
        "dataPolicy",
      ],
      "$",
      errors,
    )
  )
    return { valid: false, errors };
  if (value.contractVersion !== CONTRACT_VERSION)
    errors.push("$.contractVersion is unsupported");
  if (value.snapshotVersion !== SNAPSHOT_VERSION)
    errors.push("$.snapshotVersion is unsupported");
  validateComponentVersions(value.componentVersions, errors);
  if (!PROJECT_ID.test(value.projectId ?? ""))
    errors.push("$.projectId is invalid");

  if (
    exactKeys(
      value.conference,
      [
        "key",
        "name",
        "timezone",
        "startsAt",
        "endsAt",
        "status",
        "objective",
        "iconUrl",
        "logoUrl",
      ],
      "$.conference",
      errors,
      ["iconUrl"],
    )
  ) {
    if (!CONFERENCE_KEY.test(value.conference.key ?? ""))
      errors.push("$.conference.key is invalid");
    if (
      typeof value.conference.name !== "string" ||
      !value.conference.name.trim() ||
      value.conference.name.length > 160
    )
      errors.push("$.conference.name is invalid");
    if (
      typeof value.conference.timezone !== "string" ||
      !value.conference.timezone
    )
      errors.push("$.conference.timezone is invalid");
    if (!validDate(value.conference.startsAt))
      errors.push("$.conference.startsAt is invalid");
    if (!validDate(value.conference.endsAt))
      errors.push("$.conference.endsAt is invalid");
    if (
      validDate(value.conference.startsAt) &&
      validDate(value.conference.endsAt) &&
      Date.parse(value.conference.endsAt) <=
        Date.parse(value.conference.startsAt)
    )
      errors.push("$.conference.endsAt must follow startsAt");
    if (
      !["planned", "active", "ended", "archived"].includes(
        value.conference.status,
      )
    )
      errors.push("$.conference.status is invalid");
    if (
      value.conference.objective !== null &&
      (typeof value.conference.objective !== "string" ||
        value.conference.objective.length > 1000)
    )
      errors.push("$.conference.objective is invalid");
    if (
      value.conference.iconUrl !== undefined &&
      value.conference.iconUrl !== null &&
      !String(value.conference.iconUrl).startsWith("https://")
    )
      errors.push("$.conference.iconUrl must be HTTPS or null");
    if (
      value.conference.logoUrl !== null &&
      !String(value.conference.logoUrl).startsWith("https://")
    )
      errors.push("$.conference.logoUrl must be HTTPS or null");
  }

  if (
    exactKeys(
      value.connector,
      ["transport", "snapshotEndpoint", "region"],
      "$.connector",
      errors,
    )
  ) {
    if (value.connector.transport !== "https-json")
      errors.push("$.connector.transport is unsupported");
    if (!String(value.connector.snapshotEndpoint ?? "").startsWith("https://"))
      errors.push("$.connector.snapshotEndpoint must be HTTPS");
    if (!REGION.test(value.connector.region ?? ""))
      errors.push("$.connector.region is invalid");
  }

  if (
    exactKeys(
      value.capabilities,
      ["registrationMetrics", "paymentMetrics", "ga4Metrics"],
      "$.capabilities",
      errors,
    )
  ) {
    for (const name of ["registrationMetrics", "paymentMetrics", "ga4Metrics"])
      validateCapability(
        value.capabilities[name],
        `$.capabilities.${name}`,
        errors,
      );
  }
  if (
    exactKeys(
      value.dataPolicy,
      ["aggregateOnly", "personalDataProhibited"],
      "$.dataPolicy",
      errors,
    )
  ) {
    if (value.dataPolicy.aggregateOnly !== true)
      errors.push("$.dataPolicy.aggregateOnly must be true");
    if (value.dataPolicy.personalDataProhibited !== true)
      errors.push("$.dataPolicy.personalDataProhibited must be true");
  }
  scanPersonalData(value, "$", errors);
  return { valid: errors.length === 0, errors };
}

function validateSection(
  section,
  name,
  metricKeys,
  errors,
  optionalMetricKeys = [],
) {
  const path = `$.${name}`;
  if (
    !exactKeys(
      section,
      ["availability", "sourceUpdatedAt", "metrics"],
      path,
      errors,
    )
  )
    return;
  if (!["available", "stale", "unavailable"].includes(section.availability))
    errors.push(`${path}.availability is invalid`);
  if (section.availability === "unavailable") {
    if (section.sourceUpdatedAt !== null || section.metrics !== null)
      errors.push(
        `${path} must use null sourceUpdatedAt and metrics when unavailable`,
      );
    return;
  }
  if (!validDate(section.sourceUpdatedAt))
    errors.push(`${path}.sourceUpdatedAt is invalid`);
  if (!isObject(section.metrics)) {
    errors.push(`${path}.metrics must be an object`);
    return;
  }
  const allowedMetricKeys = [...metricKeys, ...optionalMetricKeys];
  for (const key of metricKeys)
    if (!(key in section.metrics))
      errors.push(`${path}.metrics.${key} is required`);
  for (const key of Object.keys(section.metrics))
    if (!allowedMetricKeys.includes(key))
      errors.push(`${path}.metrics.${key} is not allowed`);
  for (const key of allowedMetricKeys) {
    if (
      !(key in section.metrics) ||
      key === "countries" ||
      key === "dailyVisitors" ||
      key === "lifetime" ||
      key === "dailyCollections"
    )
      continue;
    if (!nonNegativeInteger(section.metrics[key]))
      errors.push(`${path}.metrics.${key} must be a non-negative integer`);
  }
}

export function validRegistrationCollections(value) {
  const dateOnly = (date) =>
    typeof date === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    Number.isFinite(Date.parse(`${date}T00:00:00Z`)) &&
    new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) === date;
  if (
    !isObject(value) ||
    Object.keys(value).sort().join(",") !==
      "days,throughDate,timezone,undatedAmountMinor,undatedInvoiceCount" ||
    value.timezone !== "Asia/Riyadh" ||
    !dateOnly(value.throughDate) ||
    !nonNegativeInteger(value.undatedInvoiceCount) ||
    !nonNegativeInteger(value.undatedAmountMinor) ||
    (value.undatedInvoiceCount === 0 && value.undatedAmountMinor !== 0) ||
    !Array.isArray(value.days) ||
    value.days.length > 10000
  )
    return false;
  let previous = "";
  let count = value.undatedInvoiceCount;
  let amount = value.undatedAmountMinor;
  for (const day of value.days) {
    if (
      !isObject(day) ||
      Object.keys(day).sort().join(",") !== "amountMinor,date,invoiceCount" ||
      !dateOnly(day.date) ||
      day.date <= previous ||
      day.date > value.throughDate ||
      !nonNegativeInteger(day.invoiceCount) ||
      day.invoiceCount === 0 ||
      !nonNegativeInteger(day.amountMinor)
    )
      return false;
    previous = day.date;
    count += day.invoiceCount;
    amount += day.amountMinor;
  }
  const span = value.days.length
    ? (Date.parse(value.throughDate) - Date.parse(value.days[0].date)) /
      86400000
    : 0;
  return (
    span < 10000 && Number.isSafeInteger(count) && Number.isSafeInteger(amount)
  );
}

export function validateSnapshot(value) {
  const errors = [];
  if (
    !exactKeys(
      value,
      [
        "contractVersion",
        "snapshotVersion",
        "componentVersions",
        "projectId",
        "conferenceKey",
        "generatedAt",
        "period",
        "currency",
        "registrations",
        "payments",
        "analytics",
      ],
      "$",
      errors,
    )
  )
    return { valid: false, errors };
  if (value.contractVersion !== CONTRACT_VERSION)
    errors.push("$.contractVersion is unsupported");
  if (value.snapshotVersion !== SNAPSHOT_VERSION)
    errors.push("$.snapshotVersion is unsupported");
  validateComponentVersions(value.componentVersions, errors);
  if (!PROJECT_ID.test(value.projectId ?? ""))
    errors.push("$.projectId is invalid");
  if (!CONFERENCE_KEY.test(value.conferenceKey ?? ""))
    errors.push("$.conferenceKey is invalid");
  if (!validDate(value.generatedAt)) errors.push("$.generatedAt is invalid");
  if (value.currency !== "SAR") errors.push("$.currency must be SAR");
  if (
    exactKeys(
      value.period,
      ["startsAt", "endsAt", "timezone"],
      "$.period",
      errors,
    )
  ) {
    if (!validDate(value.period.startsAt) || !validDate(value.period.endsAt))
      errors.push("$.period dates are invalid");
    if (
      validDate(value.period.startsAt) &&
      validDate(value.period.endsAt) &&
      Date.parse(value.period.endsAt) <= Date.parse(value.period.startsAt)
    )
      errors.push("$.period.endsAt must follow startsAt");
    if (typeof value.period.timezone !== "string" || !value.period.timezone)
      errors.push("$.period.timezone is invalid");
  }

  validateSection(
    value.registrations,
    "registrations",
    ["total", "completed", "pending", "cancelled"],
    errors,
  );
  if (value.registrations?.metrics) {
    const metrics = value.registrations.metrics;
    if (
      metrics.completed + metrics.pending + metrics.cancelled !==
      metrics.total
    )
      errors.push("$.registrations.metrics categories must equal total");
  }
  validateSection(
    value.payments,
    "payments",
    [
      "registrationRevenueCollectedMinor",
      "invoicesIssuedMinor",
      "invoicesOutstandingMinor",
      "collectedInvoiceCount",
      "outstandingInvoiceCount",
    ],
    errors,
    ["dailyCollections"],
  );
  const collections = value.payments?.metrics?.dailyCollections;
  if (collections !== undefined) {
    if (!validRegistrationCollections(collections))
      errors.push("$.payments.metrics.dailyCollections is invalid");
    else if (
      collections.days.reduce(
        (n, day) => n + day.invoiceCount,
        collections.undatedInvoiceCount,
      ) > value.payments.metrics.collectedInvoiceCount ||
      collections.days.reduce(
        (n, day) => n + day.amountMinor,
        collections.undatedAmountMinor,
      ) > value.payments.metrics.registrationRevenueCollectedMinor
    ) {
      errors.push(
        "$.payments.metrics.dailyCollections exceeds collected totals",
      );
    }
  }
  if (
    value.payments?.metrics?.invoicesOutstandingMinor >
    value.payments?.metrics?.invoicesIssuedMinor
  )
    errors.push(
      "$.payments.metrics outstanding amount cannot exceed issued amount",
    );
  validateSection(
    value.analytics,
    "analytics",
    [
      "activeUsersToday",
      "activeUsersYesterday",
      "activeUsersThisMonth",
      "countries",
    ],
    errors,
    ["activeUsersLast30Minutes", "dailyVisitors", "lifetime"],
  );
  if (value.analytics?.metrics) {
    if (
      "lifetime" in value.analytics.metrics &&
      !validAnalyticsLifetime(value.analytics.metrics.lifetime)
    )
      errors.push("$.analytics.metrics.lifetime is invalid");
    const seen = new Set();
    if (!Array.isArray(value.analytics.metrics.countries))
      errors.push("$.analytics.metrics.countries must be an array");
    else
      for (const [
        index,
        country,
      ] of value.analytics.metrics.countries.entries()) {
        const path = `$.analytics.metrics.countries[${index}]`;
        if (!exactKeys(country, ["countryCode", "visitors"], path, errors))
          continue;
        if (!COUNTRY.test(country.countryCode ?? ""))
          errors.push(`${path}.countryCode is invalid`);
        if (seen.has(country.countryCode))
          errors.push(`${path}.countryCode is duplicated`);
        seen.add(country.countryCode);
        if (!nonNegativeInteger(country.visitors))
          errors.push(`${path}.visitors must be a non-negative integer`);
      }
    if ("dailyVisitors" in value.analytics.metrics) {
      const dailyVisitors = value.analytics.metrics.dailyVisitors;
      const seenDates = new Set();
      if (!Array.isArray(dailyVisitors))
        errors.push("$.analytics.metrics.dailyVisitors must be an array");
      else
        for (const [index, point] of dailyVisitors.entries()) {
          const path = `$.analytics.metrics.dailyVisitors[${index}]`;
          if (!exactKeys(point, ["date", "visitors"], path, errors)) continue;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(point.date ?? ""))
            errors.push(`${path}.date is invalid`);
          if (seenDates.has(point.date))
            errors.push(`${path}.date is duplicated`);
          seenDates.add(point.date);
          if (!nonNegativeInteger(point.visitors))
            errors.push(`${path}.visitors must be a non-negative integer`);
        }
    }
  }
  scanPersonalData(value, "$", errors);
  return { valid: errors.length === 0, errors };
}

export function validateConnectorPair(manifest, snapshot) {
  const manifestResult = validateManifest(manifest);
  const snapshotResult = validateSnapshot(snapshot);
  const errors = [...manifestResult.errors, ...snapshotResult.errors];
  if (manifest.projectId !== snapshot.projectId)
    errors.push("manifest and snapshot projectId disagree");
  if (manifest.conference?.key !== snapshot.conferenceKey)
    errors.push("manifest and snapshot conference key disagree");
  for (const component of ["connector", "adapter", "source"]) {
    if (
      manifest.componentVersions?.[component] !==
      snapshot.componentVersions?.[component]
    ) {
      errors.push(`manifest and snapshot ${component} version disagree`);
    }
  }
  const mappings = [
    ["registrationMetrics", "registrations"],
    ["paymentMetrics", "payments"],
    ["ga4Metrics", "analytics"],
  ];
  for (const [capability, section] of mappings) {
    if (
      manifest.capabilities?.[capability]?.supported === false &&
      snapshot[section]?.availability !== "unavailable"
    )
      errors.push(
        `${section} must be unavailable when its capability is unsupported`,
      );
  }
  return { valid: errors.length === 0, errors };
}
